<template>
  <lateral-modal
    :title="$t(customFieldModalHeaderLabel)"
    @close="closeModal"
  >
    <template slot="content">
      <form
        id="custom-field-form"
        @submit.prevent="handleChangeConfirmation"
      >
        <div class="row">
          <div class="col-12">
            <div class="input--block input--block--margin">
              <label class="form--label u-required"> {{$t('company_create_transaction_field_name').toUpperCase()}}</label>
              <div
                class="Form-field"
                :class="{'Form--field-error': $v.customField.name.$error}"
              >
                <input
                  class="Input-text"
                  type="text"
                  v-model="customField.name"
                  @blur="$v.customField.name.$touch()"
                  :placeholder="$t('company_create_transaction_field_name_placeholder')"
                >
              </div>
            </div>
          </div>
          <div class="col-12">
            <div class="input--block input--block--margin h-100">
              <label
                for=""
                class="form--label"
              > {{$t('company_create_transaction_field_description').toUpperCase()}}</label>
              <div class="Form-field">
                <textarea
                  class="Input-textarea"
                  :placeholder="$t('company_create_transaction_field_description_placeholder')"
                  v-model="customField.description"
                ></textarea>
              </div>
            </div>
          </div>
          <div class="col-12 my-1">
            <label
              for=""
              class="form--label"
              :class="{'u-required' : !customField.customOptionEnabled}"
            >{{$t('company_create_transaction_field_option_settings').toUpperCase()}}</label>
            <div class="multiselect-custom-field ">
              <option-settings-input
                allowTaggable
                :overrideValue="customField.tags"
                :alternateOptions="customField.tags"
                :showOptions="false"
                :noOptionsLabel="'company_create_transaction_field_options_no_options'"
                :placeholder="$t('company_create_transaction_field_option_settings_placeholder')"
                :internalSearch="false"
                :class="tagsErrorClass"
                :tag-placeholder="$t('company_create_transaction_field_option_settings_placeholder')"
                @close="$v.customField.tags.$touch()"
                @tag="createOption"
                @remove="removeOption"
              ></option-settings-input>
            </div>
            <div class="input--toggle-block">

              <toggle-switch
                :id="'allow-custom-options'"
                :status.sync="customField.customOptionEnabled"
                :label="'company_create_transaction_field_allow_custom_options'"
                :labelClass="'Label Label--text'"
              ></toggle-switch>

              <toggle-switch
                :id="'allow-multiselect'"
                :status.sync="customField.multipleOptionsEnabled"
                :label="'company_create_transaction_field_allow_multiselect'"
                :labelClass="'Label Label--text my-2'"
              ></toggle-switch>

            </div>
          </div>

          <div class="col-12 my-1">
            <label
              for=""
              class="form--label u-required"
            >{{$t('company_create_custom_field_include_in_transaction_type').toUpperCase()}}</label>
            <div class="multiselect-custom-field ">
              <option-settings-input
                :alternateOptions="formatTransactionTypeIncludeIn(tagTypeIncludeInOptions)"
                :overrideValue="customField.includeInTransactionTypes"
                showOptions
                allowMultiple
                :noOptionsLabel="'company_create_transaction_field_options_no_options'"
                :placeholder="$t('company_create_transaction_field_option_settings_placeholder')"
                :internalSearch="false"
                :class="includeInTransactionTypesErrorClass"
                :tag-placeholder="$t('company_create_transaction_field_option_settings_placeholder')"
                @select="addTransactionType($event)"
                @remove="removeTransactionType($event)"
                @close="$v.customField.includeInTransactionTypes.$touch()"
              ></option-settings-input>
            </div>
          </div>
          <div class="col-12 my-1">
            <div>
              <label
                for=""
                class="form--label u-required"
              >{{$t('company_create_transaction_field_budgets_include_in').toUpperCase()}}</label>
              <div class="d-flex flex-column my-2">
                <input-radio
                  :label="'company_create_transaction_field_include_in_all_budgets'"
                  :value="'ALL_BUDGETS'"
                  :itemValue="customField.includeIn"
                  :extraInfo="'company_create_transaction_field_include_in_all_budgets_explanation'"
                  @click="setField('includeIn', $event)"
                ></input-radio>
                <small
                  class="include-in-all--explanation"
                  v-t.preserve="{path: 'company_create_transaction_field_include_in_all_budgets_explanation'}"
                ></small>
              </div>
              <div class="d-flex flex-column my-2">
                <input-radio
                  :label="'company_create_transaction_field_include_in_selected_budgets'"
                  :value="'SELECTED_BUDGETS'"
                  :itemValue="customField.includeIn"
                  @click="setField('includeIn', $event)"
                ></input-radio>
                <div
                  class="multiselect-custom-field mt-2"
                  v-if="customField.includeIn === 'SELECTED_BUDGETS'"
                >
                  <option-settings-input
                    :placeholder="$t('company_create_transaction_field_select_budget')"
                    :noOptionsLabel="'company_create_transaction_field_no_budgets'"
                    :alternateOptions="budgets"
                    :overrideValue="customField.includeBudgets"
                    @select="addBudget('includeBudgets', $event)"
                    :class="includeBudgetsErrorClass"
                    @close="$v.customField.includeBudgets.$touch()"
                    @remove="removeBudget('includeBudgets', $event)"
                    internalSearch
                    closeOnSelect
                  ></option-settings-input>
                </div>
              </div>
            </div>
          </div>
          <div class="col-12 my-2">
            <div>
              <label
                for=""
                class="form--label u-required"
              >{{$t('company_create_transaction_field_require').toUpperCase()}}</label>
              <div class="my-2">
                <input-radio
                  :label="'company_create_transaction_field_require_never'"
                  :value="'NEVER'"
                  :itemValue="customField.require"
                  @click="setField('require', $event)"
                ></input-radio>
              </div>
              <div class="my-2">
                <input-radio
                  :label="'company_create_transaction_field_require_always'"
                  :value="'ALWAYS'"
                  :itemValue="customField.require"
                  @click="setField('require', $event)"
                ></input-radio>

              </div>

              <div class="d-flex flex-column mt-2">
                <input-radio
                  :label="'company_create_transaction_field_require_selected_budgets'"
                  :value="'SELECTED_BUDGETS'"
                  :itemValue="customField.require"
                  @click="setField('require', $event)"
                ></input-radio>
                <div
                  class="multiselect-custom-field mt-2"
                  v-if="customField.require === 'SELECTED_BUDGETS'"
                >
                  <option-settings-input
                    :placeholder="$t('company_create_transaction_field_select_budget')"
                    :noOptionsLabel="'company_create_transaction_field_no_budgets'"
                    :alternateOptions="requiredBudgetsOptionList"
                    :overrideValue="customField.requireBudgets"
                    @select="addBudget('requireBudgets', $event)"
                    :class="requireBudgetsErrorClass"
                    @close="$v.customField.requireBudgets.$touch()"
                    @remove="removeBudget('requireBudgets', $event)"
                    internalSearch
                    closeOnSelect
                  ></option-settings-input>
                </div>
              </div>
            </div>
          </div>

        </div>
      </form>
    </template>
    <template slot="footer">
      <div class="d-flex">
        <button
          class="Fuell-Button Fuell-button--small danger"
          @click="closeModal"
          v-t.preserve="{path: 'ACTION_CANCEL'}"
        >
        </button>
        <button
          class="Fuell-Button block ml-2 blue"
          :disabled="!canBeSubmitted"
          type="submit"
          form="custom-field-form"
        >
          {{$t(customFieldModalConfirmActionLabel)}}
        </button>
      </div>
    </template>
  </lateral-modal>
</template>

<script>
import transactionsApi from '@/modules/transactions/_api'
import _modalHandlerMixin from '@/components/Commons/_mixins/_modalHandlerMixin'
import ToggleSwitch from '@/components/Commons/Forms/ToggleSwitch'
import OptionSettingsInput from '../_shared/OptionSettingsInput'
import InputRadio from '@/components/Commons/Forms/InputRadio'
import LateralModal from '@/components/Commons/Modals/LateralModal'

import { isEqual } from 'lodash-es'
const { validationMixin } = require('vuelidate')
const { required, minLength, requiredIf } = require('vuelidate/lib/validators')
export default {
  name: 'CreateTransactionFieldModal',
  mixins: [_modalHandlerMixin, validationMixin],
  components: { ToggleSwitch, InputRadio, OptionSettingsInput, LateralModal },
  props: {
    customFieldId: {
      type: [Number, String]
    }
  },
  data () {
    return {
      customField: {
        name: '',
        description: '',
        multipleOptionsEnabled: false,
        tags: [],
        customOptionEnabled: false,
        includeIn: 'ALL_BUDGETS',
        includeBudgets: [],
        require: 'NEVER',
        requireBudgets: [],
        includeInTransactionTypes: []
      },
      originalCustomField: {},
      hasTransactionsWithCurrentCustomField: false,
      optionId: 1
    }
  },
  computed: {
    customFieldToEdit () {
      return this.customFieldId && this.customFieldId > 0 ? this.$store.getters['CompanyStore/TransactionFieldsStore/transactionFields'].find(el => el.id === this.customFieldId) : false
    },
    budgets () {
      return this.$store.getters['BudgetsStore/budgets'] || []
    },
    tagsErrorClass () {
      return this.$v.customField.tags.$error ? 'multiselect--error' : ''
    },
    includeBudgetsErrorClass () {
      return this.$v.customField.includeBudgets.$error ? 'multiselect--error' : ''
    },
    requireBudgetsErrorClass () {
      return this.$v.customField.requireBudgets.$error ? 'multiselect--error' : ''
    },
    includeInTransactionTypesErrorClass () {
      return this.$v.customField.includeInTransactionTypes.$error ? 'multiselect--error' : ''
    },
    canBeSubmitted () {
      if (this.customFieldId) {
        return !this.$v.invalid && this.hasLocalChanges
      } else {
        return !this.$v.$invalid
      }
    },
    customFieldModalHeaderLabel () {
      return this.customFieldId ? 'company_update_transaction_field_modal_header' : 'company_create_transaction_field_modal_header'
    },
    customFieldModalConfirmActionLabel () {
      return this.customFieldId ? 'company_create_transaction_field_action_update_field' : 'company_create_transaction_field_action_create_field'
    },
    hasLocalChanges () {
      return this.customFieldId ? !isEqual(this.originalCustomField, this.customField) : false
    },
    requiredBudgetsOptionList () {
      if (this.customField.includeIn === 'SELECTED_BUDGETS') {
        return this.customField.includeBudgets
      }
      return this.budgets
    },
    tagTypeIncludeInOptions () {
      return this.$store.getters['ConfigStore/privateConfig'].tag_type_include_in_transaction_types_options || []
    }
  },
  methods: {
    setField (fieldName, event) {
      this.customField[fieldName] = event
    },
    createOption (newOption) {
      const { name } = newOption
      this.customField.tags.push({ name: name })
    },
    addTransactionType (transactionType) {
      this.customField.includeInTransactionTypes.push(transactionType)
    },
    removeTransactionType (transactionTypeId) {
      this.customField.includeInTransactionTypes = this.customField.includeInTransactionTypes.filter(el => el.id !== transactionTypeId)
    },
    addBudget (budgetField, budget) {
      const { id, name } = budget
      this.customField[budgetField].push({ id: id, name: name })
      if (budgetField === 'includeBudgets' && this.customField.require === 'SELECTED_BUDGETS') {
        this.customField.requireBudgets.push({ id: id, name: name })
      }
    },
    removeBudget (budgetField, budgetId) {
      this.customField[budgetField] = this.customField[budgetField].filter(el => el.id !== budgetId)
      if (this.customField.require === 'SELECTED_BUDGETS') {
        this.customField.requireBudgets = this.customField.requireBudgets.filter(el => el.id !== budgetId)
      }
    },
    removeOption (option) {
      this.customField.tags = this.customField.tags.filter(el => el.name !== option)
    },
    formatTransactionTypeIncludeIn (transactionIncludeInOptions) {
      return transactionIncludeInOptions.map((el, index) => {
        return {
          id: index + 1,
          code: el,
          name: this.$t(el)
        }
      })
    },
    async fetchBudgets () {
      const filters = {
        'status.code': 'ACTIVE',
        pagination: false
      }
      await this.$store.dispatch('BudgetsStore/setBudgetsFilters', filters)
    },
    async handleChangeConfirmation () {
      if (this.customFieldToEdit && this.hasLocalChanges && this.hasTransactionsWithCurrentCustomField) {
        // call modal etc..
        this.$emit('showConfirmModal', this.customField)
      } else {
        await this.submit()
      }
    },
    async checkIfHasTransactionsForCurrentTagType () {
      if (this.customFieldId) {
        let filters = {
          'tagType': this.customFieldToEdit.id
        }
        const response = await transactionsApi.getTransactions(1, true, filters)
        this.hasTransactionsWithCurrentCustomField = response.data.length > 0
      }
    },
    async submit () {
      this.$v.$touch()
      if (!this.canBeSubmitted) return
      let customField = {}

      Object.keys(this.customField).forEach(key => {
        if (this.customField[key]) {
          customField[key] = this.customField[key]
        }
      })
      if (customField.includeBudgets.length > 0) {
        customField.includeBudgets = customField.includeBudgets.map(el => { return { id: el.id } })
      }
      if (customField.requireBudgets.length > 0) {
        customField.requireBudgets = customField.requireBudgets.map(el => { return { id: el.id } })
      }

      if (customField.includeInTransactionTypes.length > 0) {
        customField.includeInTransactionTypes = customField.includeInTransactionTypes.map(el => el.code)
      }

      if (!this.customFieldId) {
        await this.$store.dispatch('CompanyStore/TransactionFieldsStore/createTransactionField', customField)
      } else {
        await this.$store.dispatch('CompanyStore/TransactionFieldsStore/updateTransactionField', customField)
      }
      this.closeModal()
    },
    _initialize () {
      if (this.customFieldId && this.customFieldToEdit) {
        this.originalCustomField = { ...this.customFieldToEdit }
        Object.keys(this.customFieldToEdit).forEach(key => {
          if (this.customFieldToEdit[key]) {
            this.customField[key] = this.customFieldToEdit[key]
          }
        })
        this.$v.$touch()
        this.customField.includeInTransactionTypes = this.formatTransactionTypeIncludeIn(this.customField.includeInTransactionTypes)
        this.checkIfHasTransactionsForCurrentTagType()
      }
    }

  },
  watch: {
    customFieldId: function (newV, oldV) {
      if (newV > 0) {
        this._initialize()
      }
    },
    'customField.includeIn': function (newV, oldV) {
      if (newV === 'ALL_BUDGETS') {
        if (this.customField.includeIn.length > 0) {
          this.customField.includeBudgets = []
        }

        /*
        if (this.customField.requireBudgets.length > 0) {
          this.customField.require = 'NEVER'
          this.customField.requireBudgets = []
        } */
      }
      if (newV === 'SELECTED_BUDGETS') {
        this.customField.requireBudgets = []
      }
    },
    'customField.require': function (newV, oldV) {
      if (newV === 'ALWAYS' || newV === 'NEVER') {
        if (this.customField.requireBudgets.length > 0) {
          this.customField.requireBudgets = []
        }
      }
      if (newV === 'SELECTED_BUDGETS' && this.customField.includeIn === 'SELECTED_BUDGETS') {
        this.customField.requireBudgets = [...this.customField.includeBudgets]
      }
    }
  },
  validations: {
    customField: {
      name: { required },
      tags: {
        required: requiredIf(function (customField) {
          return customField.customOptionEnabled === false
        })
      },

      includeIn: { required },
      includeBudgets: {
        required: requiredIf(function (customField) {
          return customField.includeIn === 'SELECTED_BUDGETS'
        })
      },
      require: { required },
      requireBudgets: {
        required: requiredIf(function (customField) {
          return customField.require === 'SELECTED_BUDGETS'
        })
      },
      includeInTransactionTypes: { required, minLength: minLength(1) }

    }
  },
  async created () {
    await this.fetchBudgets()
    if (this.customFieldId) {
      this._initialize()
    }
  },

  beforeRouteUpdate (to, from, next) {
    this.closeModal()
    next()
  }

}
</script>
<style scoped>
.multiselect--error {
  border: 2px solid red;
  border-radius: 6px;
}

.include-in-all--explanation {
  margin-left: 2rem;
  color: var(--gray);
}
.Input-textarea {
  max-height: 113px;
}
.input--toggle-block {
  margin-top: 5px;
}
</style>
